import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useReducedMotion } from 'framer-motion';
import {
  containerVariants,
  gridVariants,
  itemVariants,
  viewportOnce,
} from '../../lib/motion';

/**
 * Section-level stagger container. Children wrapped in <Item> spring in one
 * after another the first time the section scrolls into view.
 */
export const Stagger = ({ children, className, style, dense = false, as = 'div', id }) => {
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      id={id}
      className={className}
      style={style}
      variants={dense ? gridVariants : containerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={viewportOnce}
    >
      {children}
    </Comp>
  );
};

export const Item = ({ children, className, style, as = 'div', ...rest }) => {
  const Comp = motion[as] || motion.div;
  return (
    <Comp className={className} style={style} variants={itemVariants} {...rest}>
      {children}
    </Comp>
  );
};

/** A single element that springs in on its own, without a parent container. */
export const Reveal = ({ children, className, style, delay = 0, as = 'div' }) => {
  const Comp = motion[as] || motion.div;
  return (
    <Comp
      className={className}
      style={style}
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={viewportOnce}
      transition={{ type: 'spring', stiffness: 100, damping: 12, delay }}
    >
      {children}
    </Comp>
  );
};

/**
 * Headline reveal: each line rises out of its own overflow mask.
 * `lines` is an array of nodes — one per visual line.
 *
 * The mask carries padding (with compensating negative margin) so Romanian
 * diacritics and descenders are never clipped once the line has landed.
 */
export const MaskLines = ({ lines, className, as = 'h1', inView = false, delay = 0 }) => {
  const Comp = as;
  const reduced = useReducedMotion();

  const from = reduced ? { opacity: 0 } : { y: '110%', opacity: 0 };
  const to = reduced ? { opacity: 1 } : { y: '0%', opacity: 1 };

  return (
    <Comp className={className}>
      {lines.map((line, i) => {
        const transition = {
          duration: reduced ? 0.4 : 0.9,
          ease: [0.16, 1, 0.3, 1],
          delay: delay + i * 0.12,
        };
        const anim = inView
          ? { whileInView: to, viewport: viewportOnce }
          : { animate: to };
        return (
          <span key={i} className="mask-line">
            <motion.span
              className="mask-line-inner"
              initial={from}
              transition={transition}
              {...anim}
            >
              {line}
            </motion.span>
          </span>
        );
      })}
    </Comp>
  );
};

/**
 * Scroll-linked parallax, the same shape the Ortexo break sections use:
 * content drifts against the scroll and fades at both edges of the viewport.
 */
export const ParallaxBlock = ({
  children,
  className,
  style,
  distance = 50,
  fade = true,
  scaleRange = null,
}) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start end', 'end start'] });

  const y = useTransform(scrollYProgress, [0, 1], [distance, -distance]);
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(
    scrollYProgress,
    [0, 0.5, 1],
    scaleRange || [0.95, 1, 0.95]
  );

  const motionStyle = reduced
    ? style
    : { ...style, y, scale, ...(fade ? { opacity } : {}) };

  return (
    <motion.div ref={ref} className={className} style={motionStyle}>
      {children}
    </motion.div>
  );
};

/**
 * Hero content that drifts up and dissolves as the reader scrolls past it —
 * the cinematic hand-off into the next section.
 */
export const HeroScrollFade = ({ children, className, style }) => {
  const ref = useRef(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ['start start', 'end start'] });

  const y = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const opacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  return (
    <motion.div ref={ref} className={className} style={reduced ? style : { ...style, y, opacity }}>
      {children}
    </motion.div>
  );
};

export { motion };
